import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect
} from 'react';
import { useLocation, useMatch } from 'react-router';

import { NavigationBarItem, TabType } from '@/components/NavigationBar/types';
import { navigationPaths } from '@/constants/navigationPaths';

type NavigationBarContextType = {
  breadcrumbArray: NavigationBarItem[];
  setCustomBreadcrumbArray: (items: NavigationBarItem) => void;
  setBreadcrumbOverride: (items: NavigationBarItem[] | null) => void;
  tabsSegment: TabType[];
  setTabsSegment: (tabs: TabType[]) => void;
  activeTabId: string;
  setActiveTabId: (tabId: string) => void;
};

const NavigationBarContext = createContext<NavigationBarContextType>({
  breadcrumbArray: [],
  setCustomBreadcrumbArray: () => {},
  setBreadcrumbOverride: () => {},
  tabsSegment: [],
  setTabsSegment: () => {},
  activeTabId: '',
  setActiveTabId: () => {}
});

export const NavigationBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const location = useLocation();
  const [breadcrumbArray, setBreadcrumb] = useState<NavigationBarItem[]>([]);
  const [customBreadcrumbArray, setCustomBreadcrumbArray] = useState({});
  const [breadcrumbOverride, setBreadcrumbOverride] = useState<
    NavigationBarItem[] | null
  >(null);
  const [tabsSegment, setTabsSegment] = useState<TabType[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>('');
  const turbineDetailsMatch = !!useMatch(navigationPaths.turbinesDetailsTab);

  const autoBreadcrumb = useMemo(() => {
    const paths = location.pathname.split('/').filter(Boolean);
    return paths.map((segment, index) => ({
      label:
        segment === 'admin-tool'
          ? 'Admin'
          : segment.charAt(0).toUpperCase() + segment.slice(1),
      path: '/' + paths.slice(0, index + 1).join('/')
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (!turbineDetailsMatch) {
      setBreadcrumbOverride(null);
    }
  }, [location.pathname, turbineDetailsMatch]);

  useEffect(() => {
    if (breadcrumbOverride) {
      setBreadcrumb(breadcrumbOverride);
      return;
    }
    if (turbineDetailsMatch) {
      setBreadcrumb(autoBreadcrumb.slice(0, -1));
      return;
    }
    if (Object.keys(customBreadcrumbArray).length > 0) {
      setBreadcrumb([
        ...autoBreadcrumb.slice(0, -1),
        customBreadcrumbArray as NavigationBarItem
      ]);
    } else {
      setBreadcrumb(autoBreadcrumb);
    }
  }, [
    autoBreadcrumb,
    customBreadcrumbArray,
    breadcrumbOverride,
    turbineDetailsMatch
  ]);

  return (
    <NavigationBarContext.Provider
      value={{
        breadcrumbArray,
        setCustomBreadcrumbArray,
        setBreadcrumbOverride,
        tabsSegment,
        setTabsSegment,
        activeTabId,
        setActiveTabId
      }}
    >
      {children}
    </NavigationBarContext.Provider>
  );
};

export const useNavigationBar = () => useContext(NavigationBarContext);
